import React, { useEffect, useRef } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import MobilePlayerBar from '../MobilePlayerBar/index';
import DesktopPlayerBar from './DesktopPlayerBar';
import { useAuth } from '../../contexts/AuthContext';
import { usePlayerStore } from '../../stores/playerStore';
import { doc, updateDoc, increment, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { safeSetItem } from '../../utils/localStorageHelpers';

const BATCH_UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_WRITES_PER_HOUR = 100;
const MAX_RECENT_STATIONS = 10;

interface LocalListeningData {
  userId: string | null;
  listeningTime: number;
  lastSyncTime: number;
  writesThisHour: number;
  lastWriteReset: number;
  recentlyPlayed: string[];
  exploredStations: Set<string>;
}

const PlayerBar: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useAuth();
  const { currentStation, isPlaying } = usePlayerStore();
  const localDataRef = useRef<LocalListeningData>({
    userId: null,
    listeningTime: 0,
    lastSyncTime: Date.now(),
    writesThisHour: 0,
    lastWriteReset: Date.now(),
    recentlyPlayed: [],
    exploredStations: new Set()
  });

  // Load local data on mount
  useEffect(() => {
    const storedData = localStorage.getItem('playerData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData.userId === user?.uid) {
        localDataRef.current = {
          ...parsedData,
          recentlyPlayed: parsedData.recentlyPlayed || [],
          exploredStations: new Set(parsedData.exploredStations || [])
        };
      } else {
        // Reset data if user changed
        localDataRef.current = {
          userId: user?.uid || null,
          listeningTime: 0,
          lastSyncTime: Date.now(),
          writesThisHour: 0,
          lastWriteReset: Date.now(),
          recentlyPlayed: [],
          exploredStations: new Set()
        };
      }
    }
  }, [user?.uid]);

  const saveLocalData = () => {
    safeSetItem(
      'playerData',
      JSON.stringify({
        ...localDataRef.current,
        exploredStations: Array.from(localDataRef.current.exploredStations)
      }),
      {
        context: 'PlayerBar.saveLocalData',
        onQuotaExceeded: () => {
          console.info('PlayerBar.saveLocalData: Clearing stored player data after quota exceed.');
          try {
            localStorage.removeItem('playerData');
          } catch (cleanupError) {
            console.warn(
              'PlayerBar.saveLocalData: Failed to clear stored player data after quota exceed.',
              cleanupError
            );
          }
        }
      }
    );
  };

  // Update recently played and explored stations when station changes
  useEffect(() => {
    if (currentStation && isPlaying && user) {
      const stationId = currentStation.stationuuid;
      const isNewStation = !localDataRef.current.exploredStations.has(stationId);

      // Update local recently played
      localDataRef.current.recentlyPlayed = [
        stationId,
        ...localDataRef.current.recentlyPlayed.filter(id => id !== stationId)
      ].slice(0, MAX_RECENT_STATIONS);

      // Update explored stations if this is a new station
      if (isNewStation) {
        localDataRef.current.exploredStations.add(stationId);

        // Update Firestore with new station explored
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          recently_played: arrayUnion(stationId),
          stations_explored: increment(1)
        }).catch(error => {
          console.error('Error updating user data:', error);
        });
      } else {
        // Just update recently played if not a new station
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          recently_played: arrayUnion(stationId)
        }).catch(error => {
          console.error('Error updating recently played:', error);
        });
      }

      saveLocalData();
    }
  }, [currentStation?.stationuuid, isPlaying, user]);

  const syncWithFirestore = async () => {
    if (!user) return;

    const now = Date.now();
    const hoursSinceLastReset = (now - localDataRef.current.lastWriteReset) / (60 * 60 * 1000);

    // Reset write count if an hour has passed
    if (hoursSinceLastReset >= 1) {
      localDataRef.current.writesThisHour = 0;
      localDataRef.current.lastWriteReset = now;
    }

    // Check if we can write to Firestore
    if (localDataRef.current.writesThisHour >= MAX_WRITES_PER_HOUR) {
      console.log('Write limit reached, skipping Firestore update');
      saveLocalData();
      return;
    }

    try {
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', user.uid);

      // Update listening time using increment
      if (localDataRef.current.listeningTime > 0) {
        batch.update(userRef, {
          listening_time: increment(Math.floor(localDataRef.current.listeningTime)),
          last_played: new Date(),
          most_played_station: currentStation?.name || null,
          recently_played: localDataRef.current.recentlyPlayed.slice(0, MAX_RECENT_STATIONS)
        });

        // Reset local listening time after successful sync
        localDataRef.current.listeningTime = 0;
        localDataRef.current.lastSyncTime = now;
        localDataRef.current.writesThisHour++;
      }

      await batch.commit();
      saveLocalData();
    } catch (error) {
      console.error('Error syncing with Firestore:', error);
      // Keep the local data for retry
      saveLocalData();
    }
  };

  // Update listening time and sync with Firestore
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPlaying && currentStation) {
      interval = setInterval(() => {
        // Update local listening time
        localDataRef.current.listeningTime += 1;
        saveLocalData();

        // Try to sync with Firestore every BATCH_UPDATE_INTERVAL
        const timeSinceLastSync = Date.now() - localDataRef.current.lastSyncTime;
        if (timeSinceLastSync >= BATCH_UPDATE_INTERVAL) {
          syncWithFirestore();
        }
      }, 1000); // Update every second
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, currentStation, user]);

  // Sync with Firestore when component unmounts
  useEffect(() => {
    return () => {
      if (localDataRef.current.listeningTime > 0) {
        syncWithFirestore();
      }
    };
  }, []);

  if (!currentStation) return null;

  return isMobile ? <MobilePlayerBar /> : <DesktopPlayerBar />;
};

export default PlayerBar; 